import axios from "axios";
import { useToast } from "@/composables/useToast";
import { router } from "@inertiajs/vue3";
import { getZiggyRoute } from "@/composables/useZiggyRoute";
import { initializeFormData, normalizeNumberValue, normalizeCheckboxValue, normalizeSelectionValue, normalizeStructuredJsonPayload } from '@/utils/formDataNormalization';

const toast = useToast();

const SUCCESS_MESSAGE = "Scheda salvata con successo";
const ERROR_MESSAGE = "Errore durante il salvataggio della scheda";
const STRUCTURED_JSON_FIELD_TYPES = new Set(["json", "geojson"]);
const SINGLE_SELECT_FIELD_TYPES = new Set(["select", "radio", "radiogroup"]);
let history = 1;

const getPrefillLinksQuerySuffix = () => {
    if (typeof window === 'undefined') {
        return '';
    }

    const params = new URLSearchParams(window.location.search || '');
    const value = params.get('prefill_links');

    return value ? `&prefill_links=${encodeURIComponent(value)}` : '';
};
const getBackUrl = (sheet, tenant, menuData) => {
    const route = getZiggyRoute();
    const section = sheet?.sections?.filter(
        (s) => s.tenant_id === tenant.id
    )[0];
    const o = menuData.filter((o) => o.id === section?.id)[0];
    return route("database.show", { slug: o?.slug, currentSheet: sheet?.name });
};

const normalizeSelectField = (node, field) => {
    if (Array.isArray(node)) {
        node.forEach((item) => normalizeSelectField(item, field));
        return;
    }

    if (!node || typeof node !== "object") return;

    Object.entries(node).forEach(([key, value]) => {
        if (key.split(".").pop() !== field.name) {
            normalizeSelectField(value, field);
            return;
        }

        node[key] = normalizeSelectionValue(value, field);
    });
};

const navigateToField = (paragraph, data) => {
    if (paragraph?.repeatable) {
        return data[paragraph.relation];
    } else {
        return data;
    }
};

const goBack = (fallBack = null, isCreatedNewScheda = false) => {
    if (window.history.length > 1 && !isCreatedNewScheda) {
        window.history.go(history * -1);
        history = 1;
    } else if (fallBack) {
        fallBack();
    } else {
        router.visit("/");
    }
};

// Inertia visits finish after the POST has completed. Keep the save pending
// until the destination has loaded, while the old form still targets create.
const visitAfterSave = (url, options = {}) => new Promise((resolve) => {
    router.visit(url, { ...options, onFinish: resolve });
});

const submit = async (
    countHistory,
    endpoint,
    data,
    sheetName,
    onClickFn,
    editing,
    isCreatedNewScheda,
    sheet,
    tenant,
    menuData,
    isSubmitting = null,
    options = {}
) => {
    const route = getZiggyRoute();
    const text = (key, fallback) => typeof options.translate === 'function'
        ? options.translate(key)
        : fallback;

    if (isSubmitting?.value) {
        return Promise.resolve(null);
    }

    if (isSubmitting) {
        isSubmitting.value = true;
    }

    try {
        const normalizedData = normalizeStructuredJsonPayload(data, sheet);
        const canMergeIntoPayload = normalizedData
            && typeof normalizedData === 'object'
            && !(typeof FormData !== "undefined" && normalizedData instanceof FormData);
        const extraPayload = {
            ...(options.extraPayload && typeof options.extraPayload === 'object'
                ? options.extraPayload
                : {}),
        };
        if (options.recordLockToken) {
            extraPayload._record_lock_token = options.recordLockToken;
        }
        // Explicit validation workflow action ("draft" | "submit"), used by the
        // save buttons of users who submit records for validation.
        if (options.validationAction) {
            extraPayload.validation_action = options.validationAction;
        }
        let payload = canMergeIntoPayload && Object.keys(extraPayload).length
            ? { ...normalizedData, ...extraPayload }
            : normalizedData;
        if (typeof FormData !== 'undefined' && normalizedData instanceof FormData && Object.keys(extraPayload).length) {
            payload = new FormData();
            normalizedData.forEach((value, key) => payload.append(key, value));
            const append = (key, value) => {
                if (Array.isArray(value) || (value && Object.getPrototypeOf(value) === Object.prototype)) {
                    Object.entries(value).forEach(([childKey, child]) => append(`${key}[${childKey}]`, child));
                } else {
                    payload.append(key, value == null ? '' : typeof value === 'boolean' ? (value ? '1' : '0') : value);
                }
            };
            Object.entries(extraPayload).forEach(([key, value]) => {
                [...new Set(payload.keys())].filter((existing) => existing === key || existing.startsWith(`${key}[`))
                    .forEach((existing) => payload.delete(existing));
                append(key, value);
            });
        }
        const response = await axios.post(endpoint, payload, {
            headers: {
                ...(options.headers ?? {}),
                ...(sheet?.metadata_revision != null
                    ? { 'X-Metadata-Revision': String(sheet.metadata_revision) }
                    : {}),
            },
        });

        if (response.status === 206 || response.data?.links?.success === false) {
            toast.error(response.data?.message ?? text('core.recordForm.messages.incomplete', 'Alcune operazioni non sono state completate'));
            if (typeof options.onIncomplete === 'function') {
                await options.onIncomplete(response);
            }
            return response;
        }

        // Staged save (record versioning): the backend stored the changes as a
        // proposed version instead of writing the live record. Stay on the
        // form and refresh the page props so the proposal banner appears.
        if (response.data?.staged) {
            toast.success(response.data.message ?? text('core.recordForm.messages.staged', 'Modifiche salvate come proposta in attesa di convalida'));
            router.reload();
            return response;
        }

        if (typeof options.onSuccess === 'function') {
            await options.onSuccess(response);
        }

        if (options.stayOnForm) {
            toast.success(text('core.recordForm.messages.saved', SUCCESS_MESSAGE));
            return response;
        }

        const saveAction = options.saveAction ?? onClickFn.value;

        if (options.redirectTo) {
            toast.success(text('core.recordForm.messages.saved', SUCCESS_MESSAGE));
            await visitAfterSave(options.redirectTo);
            return response;
        }

        if (saveAction === "continue" || saveAction === "new") {
            history =
                parseInt(route().params.history ? route().params.history : 1) +
                (countHistory ? countHistory + 1 : 0);

            const prefillQuery = saveAction !== 'new' ? getPrefillLinksQuerySuffix() : '';

            await visitAfterSave(
                `/database/${saveAction !== "new" ? "modifica-scheda" : "nuova-scheda"
                }/${sheetName}/${saveAction !== "new" ? response.data.data.id : ""
                }?new=${!editing}&history=${history}${prefillQuery}`,
                {
                    onSuccess: () => {
                        toast.success(text('core.recordForm.messages.saved', SUCCESS_MESSAGE));
                    },
                }
            );
        } else {
            history =
                parseInt(route().params.history ? route().params.history : 1) +
                (countHistory ? countHistory : 0);
            toast.success(text('core.recordForm.messages.saved', SUCCESS_MESSAGE));
            setTimeout(() => {
                goBack(
                    () => {
                        router.visit(getBackUrl(sheet, tenant, menuData));
                    },
                    isCreatedNewScheda
                );
            }, 100); // time to show the toast
        }

        return response;
    } catch (error) {
        if (error.response && error.response.status === 422) {
            const errors = error.response.data?.errors;
            const errorMessages = errors && typeof errors === 'object'
                ? Object.values(errors).flat().filter((message) => typeof message === 'string').join(', ')
                : '';
            toast.error(errorMessages || error.response.data?.message || text('core.recordForm.messages.saveFailed', ERROR_MESSAGE));
        } else if (error.response && [409, 423].includes(error.response.status)) {
            toast.error(error.response.data?.message ?? text('core.recordForm.messages.saveFailed', ERROR_MESSAGE));
        } else {
            toast.error(text('core.recordForm.messages.saveFailed', ERROR_MESSAGE));
        }

        throw error;
    } finally {
        if (isSubmitting) {
            isSubmitting.value = false;
        }
    }
};

const simplifyData = (field, key, value, dataRef) => {
    const data = dataRef.value;
    if (!data) return;

    if (field?.form_field_type_id === "number" && value !== undefined) {
        data[key] = normalizeNumberValue(value);
    }

    if (SINGLE_SELECT_FIELD_TYPES.has(field?.form_field_type_id) || field?.form_field_type_id === "multiselect") {
        normalizeSelectField(data, field);
    }

    // repeatable fields should be an array of "raw" values
    if (field.repeatable && field.repeatable_support_table) {
        const relationSegments = field.relation ? field.relation.split(".") : [];
        const targetKey = relationSegments.pop();

        if (targetKey) {
            const normalizeRepeatableValues = (node) => {
                if (!node || typeof node !== "object") {
                    return;
                }

                if (Array.isArray(node)) {
                    node.forEach((child) => normalizeRepeatableValues(child));
                    return;
                }

                Object.keys(node).forEach((key) => {
                    const value = node[key];

                    if (key === targetKey && Array.isArray(value)) {
                        node[key] = value
                            .map((item) => {
                                if (item === null || item === undefined) {
                                    return '';
                                }

                                if (typeof item === "object") {
                                    if (Object.prototype.hasOwnProperty.call(item, "name")) {
                                        return item.name ?? '';
                                    }

                                    if (Object.prototype.hasOwnProperty.call(item, field.name)) {
                                        return item[field.name] ?? '';
                                    }

                                    if (Object.prototype.hasOwnProperty.call(item, "value")) {
                                        return item.value ?? '';
                                    }

                                    const fallbackValue = Object.values(item).find((val) => typeof val === "string");
                                    return fallbackValue ?? '';
                                }

                                return item;
                            })
                            .filter((entry) => entry !== undefined);
                    } else if (value && typeof value === "object") {
                        normalizeRepeatableValues(value);
                    }
                });
            };

            normalizeRepeatableValues(data);
        }
    }

};

/**
 * Ritorna un array piatto con TUTTI i form_fields
 * presenti nei paragrafi e nei relativi sotto-paragrafi,
 * ordinato per proprietà `order`.
 *
 * @param {Array<Object>} paragraphs
 * @returns {Array<Object>}
 */
const flattenFields = (paragraphs = []) => {
    // funzione interna ricorsiva
    const walk = (nodes = []) =>
        nodes.flatMap((node) => {
            if (!node || typeof node !== 'object') {
                return [];
            }

            const fieldsHere = Array.isArray(node.form_fields) ? node.form_fields.filter(Boolean) : [];
            // ricorsione sui sotto-paragrafi
            const fieldsBelow = walk(Array.isArray(node.form_paragraphs) ? node.form_paragraphs : []);
            return [...fieldsHere, ...fieldsBelow];
        });

    // raccolta completa + ordinamento globale
    return walk(Array.isArray(paragraphs) ? paragraphs : []).sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
};


function isArrayofObjects(value) {
    return (
        Array.isArray(value) && value.length > 0 && typeof value[0] === "object"
    );
}

const updateArrayFields = (dataRef, fields) => {
    // Access the actual data object via .value
    const data = dataRef.value;
    if (!data) return;

    for (const key in data) {
        if (isArrayofObjects(data[key])) {
            const field = fields.find((field) => field.name === key);
            if (STRUCTURED_JSON_FIELD_TYPES.has(field?.form_field_type_id)) {
                continue;
            }
            // Simplify arrays of objects to arrays of IDs
            // Handles both {id, name} from backend and {value, label} from EditableSelectElement
            if (field?.form_field_type_id === 'multiselect' || field?.form_field_type_id === 'tags') {
                data[key] = data[key]
                    .map((item) => item?.id ?? item?.value)
                    .filter((val) => val !== undefined && val !== null);
            }
        }
    }
    // No need to reassign dataRef.value = data, modification happens in place
};

/**
 * Vueform checkboxes are built with trueValue: 1 / falseValue: 0, while the API can
 * return booleans (Eloquent boolean casts) or strings: without this coercion the box
 * renders unchecked even though the record holds a truthy value.
 */
const normalizeCheckboxFields = (dataRef, fields) => {
    const data = dataRef.value;
    if (!data) return;

    const checkboxNames = new Set(
        (fields ?? [])
            .filter((field) => field?.form_field_type_id === "checkbox" && field?.name)
            .map((field) => field.name)
    );

    if (!checkboxNames.size) return;

    const visit = (node) => {
        if (Array.isArray(node)) {
            node.forEach(visit);
            return;
        }

        if (!node || typeof node !== "object") return;

        Object.keys(node).forEach((key) => {
            const value = node[key];
            // Top-level keys are dot-flattened, repeatable rows keep plain keys.
            const isCheckbox = checkboxNames.has(key.split(".").pop());

            if (isCheckbox && (value === null || typeof value !== "object")) {
                node[key] = normalizeCheckboxValue(value);
                return;
            }

            if (value && typeof value === "object") {
                visit(value);
            }
        });
    };

    visit(data);
};

const restoreStructuredJsonFields = (dataRef, sourceData, fields) => {
    const data = dataRef.value;
    if (!data || !sourceData || typeof sourceData !== "object") return;

    fields
        .filter((field) => STRUCTURED_JSON_FIELD_TYPES.has(field?.form_field_type_id))
        .forEach((field) => {
            if (!field?.name || !Object.prototype.hasOwnProperty.call(sourceData, field.name)) {
                return;
            }

            if (Object.prototype.hasOwnProperty.call(data, field.name)) {
                return;
            }

            data[field.name] = sourceData[field.name];
        });
};

const errorPreview = async (checkIcon) => {
    const images = document.querySelectorAll("img");
    images.forEach((img) => {
        img.onerror = function () {
            this.onerror = null;
            this.src = checkIcon;
            img.classList.add("!w-11", "!h-11", "bg-[#94969A]", "p-3");
        };
        if (img.src === "") {
            img.onerror = null;
            img.src = checkIcon;
            img.classList.add("!w-11", "!h-11", "bg-[#94969A]", "p-3");
        }
        img.style.setProperty("object-fit", "contain", "important");
        img.style.setProperty("border-radius", "0.75rem", "important");
    });
};

function extractLabels(paragraphs, maxDepth, depth = 1) {
    let results = [];

    paragraphs.forEach((p) => {
        results.push({
            label: p.label,
            deep: depth,
        });

        if (
            depth < maxDepth &&
            Array.isArray(p.form_paragraphs) &&
            p.form_paragraphs.length > 0
        ) {
            const nested = extractLabels(
                p.form_paragraphs,
                maxDepth,
                depth + 1
            );
            results = results.concat(nested);
        }
    });

    return results;
}

export {
    initializeFormData,
    submit,
    simplifyData,
    flattenFields,
    updateArrayFields,
    restoreStructuredJsonFields,
    normalizeCheckboxFields,
    normalizeStructuredJsonPayload,
    getBackUrl,
    navigateToField,
    errorPreview,
    goBack,
    visitAfterSave,
    extractLabels,
};
